import { ref } from 'vue';
import { registerAuthTeardown } from '@/utils/auth-teardown';
import { isPrivyCustodialLogin } from '@/utils/privy';
import { trackAnalyticsLog } from '@/utils/analytics-track';

/** 全局单例：启用交易弹窗（只挂 App 一次，避免详情叠层） */
const open = ref(false);
const marketKind = ref('binary');
const approvalReady = ref(false);

/** 本会话已对某地址谈过一次（关闭/完成都不再自动弹） */
const promptedByAddress = new Set();

const refreshHandlers = new Set();

function normalizeAddr(addr) {
  return String(addr ?? '')
    .trim()
    .toLowerCase();
}

/**
 * 详情预检结果同步：缺授权时全局最多弹一次，不叠加。
 * 仅自管私有钱包（MetaMask 等）弹签名授权；Privy 邮箱/Google 托管代扣，不弹窗。
 * @param {{ checked?: boolean, ready?: boolean, needsApproval?: boolean, userAddress?: string, marketKind?: string }|null} status
 */
export function syncEnableTradingPrompt(status) {
  if (!status?.checked) return;

  if (status.ready) {
    approvalReady.value = true;
    if (open.value) open.value = false;
    return;
  }

  approvalReady.value = false;
  if (!status.needsApproval) return;

  // 邮箱 / Google（Privy 托管）：后端代扣，禁止弹「启用交易」签名窗
  if (isPrivyCustodialLogin()) {
    approvalReady.value = true;
    if (open.value) open.value = false;
    return;
  }

  const addr = normalizeAddr(status.userAddress);
  if (!addr) return;
  if (promptedByAddress.has(addr)) return;
  if (open.value) return;

  promptedByAddress.add(addr);
  marketKind.value = status.marketKind === 'multipleChoice' ? 'multipleChoice' : 'binary';
  open.value = true;
  trackAnalyticsLog({
    key: 'trade.approve.open',
    label: '弹出启用交易',
    value: { marketKind: marketKind.value },
  });
}

export function setEnableTradingOpen(next) {
  open.value = next === true;
}

export function registerEnableTradingRefresh(fn) {
  if (typeof fn !== 'function') return () => {};
  refreshHandlers.add(fn);
  return () => refreshHandlers.delete(fn);
}

export async function completeEnableTradingPrompt() {
  open.value = false;
  approvalReady.value = true;
  trackAnalyticsLog({
    key: 'trade.approve.success',
    label: '启用交易成功',
    value: { marketKind: marketKind.value },
  });
  const tasks = [...refreshHandlers].map((fn) => Promise.resolve().then(() => fn()));
  await Promise.allSettled(tasks);
}

export function resetEnableTradingPrompt() {
  open.value = false;
  approvalReady.value = false;
  promptedByAddress.clear();
}

registerAuthTeardown(() => {
  resetEnableTradingPrompt();
});

/** App 壳绑定弹窗 */
export function useEnableTradingPrompt() {
  return {
    enableTradingOpen: open,
    enableTradingMarketKind: marketKind,
    enableTradingApprovalReady: approvalReady,
    setEnableTradingOpen,
    completeEnableTradingPrompt,
  };
}
